import { Box } from "@mui/material";
import React from "react";
import { useLocation } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import { AppearanceProvider, useSlidingAppearance } from "../lib/AppearanceContext";
import { SubscriptionProvider } from "../lib/SubscriptionContext";
import { useSlidingPushNotifications } from "../lib/useSlidingPushNotifications";
import {
    getSlidingSafeAreaTop,
    slidingBottomNavClearance,
    slidingHomeBottomClearance,
    slidingSafeAreaBottom,
} from "../styles/mainStyles";
import { getHomePremium, homePremiumShellSx } from "../styles/homePremiumStyles";
import { PLAY_PATH, MAIN_PATH, LIBRARY_PATH, MY_PUZZLES_PATH, SETTINGS_PATH } from "../routes";
import HomeLightBlueMist from "../components/HomeLightBlueMist";
import { slidingSurfaceSx } from "../lib/appearance";

function LayoutShell({ children, showBottomNav, isHomePage }) {
    const { tokens, isDark } = useSlidingAppearance();
    const { pathname } = useLocation();
    useSlidingPushNotifications();
    const safeAreaTop = getSlidingSafeAreaTop();
    const isLibraryPage = pathname === LIBRARY_PATH;
    const isMyPuzzlesPage = pathname === MY_PUZZLES_PATH;
    const isSettingsPage = pathname === SETTINGS_PATH;
    const isPlayPage = pathname === PLAY_PATH || pathname.startsWith(`${PLAY_PATH}/`);
    const usePremiumShell =
        isHomePage || isLibraryPage || isMyPuzzlesPage || isPlayPage || isSettingsPage;
    const showPageMist = usePremiumShell;
    const bottomClearance = showBottomNav
        ? isHomePage
            ? slidingHomeBottomClearance
            : slidingBottomNavClearance
        : slidingSafeAreaBottom;
    const homeNav = getHomePremium(isDark);

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "100dvh",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
                ...(usePremiumShell
                    ? homePremiumShellSx(isDark)
                    : {
                          ...slidingSurfaceSx(tokens, "shell"),
                          color: tokens.text,
                      }),
                overflowX: "hidden",
                pt: usePremiumShell ? 0 : safeAreaTop,
                pb: bottomClearance,
            }}
        >
            {showPageMist ? <HomeLightBlueMist isDark={isDark} /> : null}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflow: "hidden",
                }}
            >
                {children}
            </Box>
            {showBottomNav ? (
                <Box
                    component="nav"
                    aria-label="Sliding Puzzle Quest"
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        bgcolor: homeNav.navBg,
                        boxSizing: "border-box",
                    }}
                >
                    <BottomNav />
                </Box>
            ) : null}
        </Box>
    );
}

/** Sliding Puzzle Quest — full-viewport shell for `/mobile/sliding/*` (no site header). */
export default function Layout({ children }) {
    const { pathname } = useLocation();
    const showBottomNav = pathname !== PLAY_PATH && !pathname.startsWith(`${PLAY_PATH}/`);
    const isHomePage = pathname === MAIN_PATH;

    return (
        <AppearanceProvider>
            <SubscriptionProvider>
                <LayoutShell showBottomNav={showBottomNav} isHomePage={isHomePage}>
                    {children}
                </LayoutShell>
            </SubscriptionProvider>
        </AppearanceProvider>
    );
}
